import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useMe, useSignup } from '@/api/auth';
import { ApiError } from '@/api/client';
import { Button, Input } from '@/components/ui';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  companyName: z.string().trim().min(2, 'Enter your company name').max(120),
  name: z.string().trim().min(1, 'Enter your name').max(120),
  email: z.string().trim().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(10, 'Use at least 10 characters').max(128),
});
type Form = z.infer<typeof schema>;

/** Self-serve sign-up: a business creates its workspace on a 14-day free trial. */
export function SignupPage() {
  const navigate = useNavigate();
  const me = useMe();
  const signup = useSignup();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  if (me.data) return <Navigate to="/inbox" replace />;

  const onSubmit = handleSubmit((values) =>
    signup.mutate(values, {
      onSuccess: () => navigate('/settings/billing?welcome=1', { replace: true }),
      onError: (err) => {
        const fields = err instanceof ApiError ? err.fieldErrors : {};
        for (const [field, message] of Object.entries(fields))
          if (field in schema.shape) setError(field as keyof Form, { message });
      },
    }),
  );
  const fieldErrors = signup.error instanceof ApiError ? signup.error.fieldErrors : {};
  const formError = signup.error && Object.keys(fieldErrors).length === 0 ? signup.error.message : null;

  return (
    <AuthLayout
      title="Start your free trial"
      subtitle="14 days, every feature. No card needed."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 font-semibold hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Input
          label="Company name"
          autoComplete="organization"
          autoFocus
          error={errors.companyName?.message}
          {...register('companyName')}
        />
        <Input label="Your name" autoComplete="name" error={errors.name?.message} {...register('name')} />
        <Input
          label="Work email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 10 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        {formError && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}{' '}
            {signup.error instanceof ApiError && signup.error.status === 409 && (
              <Link to="/login" className="font-semibold underline">
                Sign in
              </Link>
            )}
          </p>
        )}
        <Button type="submit" className="w-full" loading={signup.isPending}>
          Create workspace
        </Button>
        <ul className="space-y-1.5 pt-2 text-xs text-slate-500">
          {[
            'AI agent that answers from your help center',
            'Live chat widget and email inbox',
            'Invite your team',
          ].map((t) => (
            <li key={t} className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {t}
            </li>
          ))}
        </ul>
      </form>
    </AuthLayout>
  );
}
